import JSZip from "jszip";
import { parseColor } from "react-stately";

import { getVisualUrl, actions } from "@app/utils";
import { convertImageBlobToType } from "@app/utils/imageBlob";
import { getAuthHeaders } from "@app/utils/apiClient";

/**
 * @param {string} hexValue
 * @returns {{ R: number, G: number, B: number } | null}
 */
function hexCampaignColorToRgbSliders(hexValue) {
  if (hexValue == null || hexValue === "") return null;
  try {
    const rgb = parseColor(hexValue).toFormat("rgb");
    return {
      R: Math.round(rgb.getChannelValue("red")),
      G: Math.round(rgb.getChannelValue("green")),
      B: Math.round(rgb.getChannelValue("blue")),
    };
  } catch {
    return null;
  }
}

/**
 * Sets MOGRT export.json RGB effect sliders from a campaign hex color.
 * @param {Record<string, object>} items - export.json section under the template top key
 * @param {string} campaignColorKey - "Brand-color" | "Secondary-color"
 * @param {string} hexValue - The hex value of the campaign color.
 */
function applyHexColorToExportEffects(items, campaignColorKey, hexValue) {
  const channels = hexCampaignColorToRgbSliders(hexValue);
  if (!channels) return;

  for (const item of Object.values(items)) {
    const effects = item?.Effects;
    if (!effects || typeof effects !== "object") continue;

    for (const [suffix, sliderValue] of Object.entries(channels)) {
      const effectKey = `${campaignColorKey}--${suffix}`;
      if (effects[effectKey]?.Slider !== undefined) {
        effects[effectKey].Slider = sliderValue;
      }
    }
  }
}

/**
 * Downloads a file from the browser.
 *
 * @param {*} content - The content to be downloaded.
 * @param {string} fileName - The name of the file to be downloaded.
 * @param {string} fileType - The type of the file to be downloaded.
 *
 * @returns {void} - Initiates a download of the file.
 */
export const downloadFile = (content, fileName, fileType) => {
  const blob = new Blob([content], {
    type: fileType,
  });
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = fileName.includes(".")
    ? fileName
    : `${fileName}.${fileType.split("/")[1]}`;
  a.style.display = "none";

  document.body.appendChild(a);

  a.click();

  URL.revokeObjectURL(url);
  a.parentElement.removeChild(a);
};

/**
 * Fetches an image from a URL and returns its blob along with metadata.
 *
 * @param {Object} image - The image object containing the URL and other metadata.
 * @param {string} image.url - The URL of the image to be fetched. Can also be a
 *   base64 data URL (e.g. "data:image/png;base64,...."), in which case the image
 *   is decoded locally instead of being fetched through the download proxy.
 *
 * @returns {Promise<Object>} - A promise that resolves to an object containing the image metadata and its blob.
 * @throws {Error} - If the image cannot be fetched or if the response is not
 */
export const getImageBlob = async (image) => {
  const url = await getVisualUrl(image);
  const isDataUrl = typeof url === "string" && url.startsWith("data:");

  const hintedType = image?.contentType || image?.mimeType;
  let response;

  if (isDataUrl) {
    // Base64/data URLs are already local content - decode them directly
    // instead of routing through the SSRF-protected download proxy, which
    // only accepts http(s) URLs.
    response = await fetch(url);
  } else {
    const headerOverrides = image?.downloadHeaders || image?.headers || {};
    const proxyUrl = actions["exd-storyteller-ai/downloads"];
    const authHeaders = await getAuthHeaders();

    response = await fetch(proxyUrl, {
      method: "POST",
      headers: {
        ...authHeaders,
        "Content-Type": "application/json",
      },
      credentials: "include",
      mode: "cors",
      body: JSON.stringify({
        url,
        headers: headerOverrides,
      }),
    });
  }

  if (!response.ok) {
    throw new Error(`Failed to fetch image: ${response.statusText}`);
  }

  let contentType =
    response.headers.get("content-type") || hintedType || "image/jpg";
  contentType = contentType.split(";")[0].trim().toLowerCase();
  if (contentType === "application/octet-stream") {
    const hintBase = hintedType?.split(";")[0].trim().toLowerCase();
    contentType =
      hintBase && hintBase.startsWith("audio/") ? hintBase : "image/jpg";
  }

  let format;
  if (
    contentType === "audio/x-wav" ||
    contentType === "audio/wav" ||
    contentType === "audio/wave"
  ) {
    format = "wav";
  } else {
    format = contentType.split("/")[1].replace(/\+.*$/, "");
  }

  const blob = await response.blob();
  let bitmap;
  let size = { width: 0, height: 0 };

  if (contentType === "image/svg+xml") {
    const convertedBlob = await convertImageBlobToType(blob, "png");
    bitmap = await window.createImageBitmap(convertedBlob);
    size.width = bitmap.width;
    size.height = bitmap.height;
  } else if (contentType.startsWith("image/")) {
    bitmap = await window.createImageBitmap(blob);
    size.width = bitmap.width;
    size.height = bitmap.height;
  }

  let name = image?.name;
  if (name == null || name === "") {
    if (!isDataUrl) {
      try {
        const urlObj = new URL(image.url);
        name = urlObj.pathname.split("/").pop().split(".")[0];
      } catch (e) {
        name = "" + crypto.randomUUID();
      }
    } else {
      name = "" + crypto.randomUUID();
    }
  }

  return {
    image: { ...image, contentType, format, size, name },
    blob,
  };
};

/**
 * Downloads an image and saves it to the user's device.
 *
 * @param {Object} image - The image object containing the URL and other metadata.
 * @param {string} [fileName] - The name of the file to be downloaded
 *
 * @returns {Promise<string>} - A promise that resolves to a success message or rejects with an error message.
 * @throws {Error} - If the image cannot be downloaded or if there is an error
 */
export const downloadImage = (image, fileName) => {
  return new Promise((resolve, reject) => {
    if (!image || !image.url) {
      return reject("No image to download");
    }

    getImageBlob(image)
      .then(({ image, blob }) => {
        downloadFile(blob, fileName ?? "image.png", image.contentType);
        resolve("Image downloaded successfully");
      })
      .catch((error) => {
        reject(`Error downloading image: ${error}`);
      });
  });
};

/**
 * Merges campaign data into a MOGRT export template.
 *
 * @param {Object} template - Parsed export.json with flat labels or nested field groups.
 * @param {Object} campaign - Flat key-value campaign data (e.g. "Company-name": "5.11 Tactical").
 * @param {string} exportFileName - "export-grouped.json" for grouped hex fields; otherwise legacy.
 * @returns {Object} - Template with Source Text and brand RGB sliders updated from campaign.
 */
export const generateExportJson = (
  template,
  campaign,
  exportFileName = "export.json",
) => {
  if (!template || !campaign || typeof template !== "object") {
    return template;
  }
  const topKey = Object.keys(template)[0];
  if (!topKey) return template;
  const cloned = JSON.parse(JSON.stringify(template));
  const items = cloned[topKey];
  if (!items || typeof items !== "object") return cloned;

  if (exportFileName !== "export-grouped.json") {
    for (const [label, value] of Object.entries(campaign)) {
      if (label.includes("color")) {
        applyHexColorToExportEffects(items, label, value);
        continue;
      }

      const item = items[label];
      if (item?.Text?.["Source Text"] !== undefined) {
        item.Text["Source Text"] =
          value === null || value === undefined ? "" : String(value);
      }
    }
    return cloned;
  }

  const fillGroupedFields = (group) => {
    for (const [label, item] of Object.entries(group)) {
      if (!item || typeof item !== "object") continue;
      if (item.Text?.["Source Text"] !== undefined) {
        if (!Object.hasOwn(campaign, label)) continue;
        const value = campaign[label];
        if (/color/i.test(label) && value != null && value !== "") {
          try {
            const color = String(value);
            item.Text["Source Text"] = parseColor(
              /^[0-9a-f]{6}$/i.test(color) ? `#${color}` : color,
            )
              .toString("hex")
              .replace(/^#/, "")
              .toUpperCase();
          } catch {
            continue;
          }
        } else {
          item.Text["Source Text"] = value == null ? "" : String(value);
        }
      } else {
        fillGroupedFields(item);
      }
    }
  };
  fillGroupedFields(cloned);
  return cloned;
};

/**
 * Downloads multiple images as a zip file.
 *
 * @param {Array} images - The array of image objects to be downloaded.
 * @param {string} fileName - The name of the zip file to be created.
 * @param {Array<{ name: string, data: object }>} [texts] - Optional array of JSON files to add to the zip.
 *
 * @returns {Promise<string>} - A promise that resolves to a success message or rejects with an error message.
 */
export const downloadImages = (images, fileName, texts) => {
  return new Promise((resolve, reject) => {
    if ((!images || images.length === 0) && (!texts || texts.length === 0)) {
      return resolve("No data to download");
    }

    const zip = new JSZip();
    const validImages = (images ?? []).filter(Boolean);
    const imagePromises = validImages.map((image, index) => {
      if (image.blob) {
        zip.file(`${image.name ?? `image_${index + 1}`}.png`, image.blob);
        return Promise.resolve();
      }

      return getImageBlob(image)
        .then(({ image: img, blob }) => {
          zip.file(`${img.name ?? `image_${index + 1}`}.${img.format}`, blob);
        })
        .catch((error) => {
          console.error(`Error downloading image ${index + 1}:`, error);
          throw error;
        });
    });

    Promise.all(imagePromises)
      .then(() => {
        if (texts?.length) {
          for (const { name, data } of texts) {
            zip.file(`${name}.json`, JSON.stringify(data, null, 2));
          }
        }
        return zip.generateAsync({ type: "blob" });
      })
      .then((content) => {
        downloadFile(
          content,
          `${fileName}.zip` ?? "images.zip",
          "application/zip",
        );
        resolve("Images downloaded successfully");
      })
      .catch((error) => {
        reject(`Error generating zip file: ${error}`);
      });
  });
};
